// src/pages/portal/reporting/eod/EodCashSections.js
// Cash-side building blocks of the EOD page, moved verbatim out of
// PortalEodPage.js when the sales-breakdown sections were added. No behaviour
// change — only the import depth differs (this file sits one level deeper).
import React, { useState, useEffect } from 'react';
import useIsMobile from '../../../../hooks/useIsMobile';
import MobileCard from '../../../../components/common/MobileCard';
import { C } from '../../../../styles/theme';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const FONT = "'Plus Jakarta Sans', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, currency) => {
    if (n == null) return `${currency}0`;
    const rounded = Math.round(n * 100) / 100;
    const abs = Math.abs(rounded);
    const str = abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${rounded < 0 ? '-' : ''}${currency}${str}`;
};

const getInitials = (name) => {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
        ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
        : parts[0].slice(0, 2).toUpperCase();
};

const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDuration = (startIso, endIso) => {
    if (!startIso) return '';
    const start = new Date(startIso);
    const end = endIso ? new Date(endIso) : new Date();
    const mins = Math.round((end - start) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${String(m).padStart(2, '0')}m`;
};

const formatDisplayDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

// ─── Modal ────────────────────────────────────────────────────────────────────
const Modal = ({ open, onClose, title, children, width = 480 }) => {
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="portal-modal-card" style={{ background: C.white, borderRadius: 12, width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', fontFamily: FONT }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, color: C.mutedLight, cursor: 'pointer', lineHeight: 1, padding: 0 }} aria-label="Close">&times;</button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const KpiCard = ({ label, value, sub, alert }) => {
    const isMobile = useIsMobile();
    return (
        <div style={{
            background: C.white, borderRadius: 12, padding: isMobile ? 14 : 20,
            border: `1px solid ${C.border}`, fontFamily: FONT, minWidth: 0,
        }}>
            <div style={{ fontSize: isMobile ? 10 : 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: C.muted, marginBottom: isMobile ? 6 : 8 }}>{label}</div>
            <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: alert ? C.red : C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
            {sub && <div style={{ fontSize: isMobile ? 11 : 12, color: C.mutedLight, marginTop: 4 }}>{sub}</div>}
        </div>
    );
};

const SectionHeader = ({ title, badge, badgeType = 'gray' }) => {
    const badgeColors = {
        green: { background: C.greenLight, color: C.green },
        red:   { background: C.redLight, color: C.red },
        gray:  { background: '#f1f5f9', color: C.muted },
    };
    const bc = badgeColors[badgeType] || badgeColors.gray;
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontFamily: FONT }}>{title}</div>
            {badge && (
                <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 20,
                    background: bc.background, color: bc.color, fontFamily: FONT,
                }}>{badge}</span>
            )}
        </div>
    );
};

const ActiveSessionCard = ({ session, currency, onForceClose, t }) => {
    const isMobile = useIsMobile();
    const stats = [
        { label: t('start_cash') || 'Start Cash', value: fmt(session.start_cash ?? 0, currency) },
        { label: t('sales') || 'Sales', value: fmt(session.total_sales ?? 0, currency) },
        { label: t('expenses') || 'Expenses', value: fmt(session.total_expenses ?? 0, currency) },
    ];
    const forceCloseBtn = (mobile) => (
        <button
            onClick={() => onForceClose(session)}
            style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: mobile ? '12px 16px' : '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', border: `1px solid #fecaca`, width: mobile ? '100%' : 'auto',
                background: C.redLight, color: C.red, transition: 'all 0.15s', flexShrink: 0,
                fontFamily: FONT, marginTop: mobile ? 12 : 0,
            }}
        >{t('force_close') || 'Force Close'}</button>
    );

    if (isMobile) {
        return (
            <MobileCard
                collapsible={false}
                accent="green"
                avatar={{ initials: getInitials(session.employee_name), tone: 'green' }}
                title={session.employee_name}
                status={{ label: t('active') || 'Active', tone: 'green' }}
                subtitle={`${session.session_id} · ${t('started') || 'Started'} ${formatTime(session.start_time)} · ${formatDuration(session.start_time)}`}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                    {stats.map((s, i) => (
                        <div key={i} style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{s.value}</div>
                            <div style={{ fontSize: 10, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                        </div>
                    ))}
                </div>
                {forceCloseBtn(true)}
            </MobileCard>
        );
    }

    return (
        <div style={{
            background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            borderLeft: `4px solid ${C.green}`, padding: '20px 24px', marginBottom: 12,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            fontFamily: FONT, transition: 'box-shadow 0.15s',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{
                    width: 40, height: 40, borderRadius: 10, background: C.greenLight,
                    color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 800, fontSize: 14,
                }}>{getInitials(session.employee_name)}</div>
                <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{session.employee_name}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                        {session.session_id} &middot; {session.sales_date || ''} &middot; {t('started') || 'Started'} {formatTime(session.start_time)} &middot; {formatDuration(session.start_time)}
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                {stats.map((s, i) => (
                    <div key={i} style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{s.value}</div>
                        <div style={{ fontSize: 10, color: C.mutedLight, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
                    </div>
                ))}
                {forceCloseBtn(false)}
            </div>
        </div>
    );
};

const CashReconBar = ({ session, currency, t }) => {
    const startCash = session.start_cash ?? 0;
    const expected = session.expected_cash ?? 0;
    const counted = session.end_cash ?? 0;
    const diff = session.cash_difference ?? (counted - expected);
    const diffColor = diff < 0 ? C.red : diff > 0 ? C.green : C.text;

    const cells = [
        { label: t('start_cash') || 'Start Cash', value: fmt(startCash, currency), bg: C.bg, color: C.text },
        { label: t('expected_cash') || 'Expected', value: fmt(expected, currency), bg: C.white, color: C.text },
        { label: t('counted_cash') || 'Counted', value: fmt(counted, currency), bg: C.white, color: C.text },
        { label: t('difference') || 'Difference', value: fmt(diff, currency), bg: C.white, color: diffColor },
    ];

    return (
        <div className="portal-grid-4" style={{
            display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1,
            background: C.border, borderRadius: 10, overflow: 'hidden', margin: '16px 0',
        }}>
            {cells.map((c, i) => (
                <div key={i} style={{ background: c.bg, padding: '14px 16px', textAlign: 'center' }}>
                    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: C.muted, marginBottom: 6 }}>{c.label}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: c.color }}>{c.value}</div>
                </div>
            ))}
        </div>
    );
};

const BreakdownRow = ({ salesBreakdown, expensesBreakdown, currency, t }) => (
    <div className="portal-grid-2" style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24,
        marginTop: 16, paddingTop: 16, borderTop: '1px solid #f1f5f9',
    }}>
        <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 10 }}>
                {t('sales_breakdown') || 'Sales Breakdown'}
            </div>
            {(salesBreakdown || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span style={{ color: '#475569' }}>{item.label}</span>
                    <span style={{ fontWeight: 700, color: C.text }}>{fmt(item.amount, currency)}</span>
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderTop: '1px solid #f1f5f9', paddingTop: 6, marginTop: 4 }}>
                <span style={{ color: '#475569', fontWeight: 700 }}>{t('total_sales') || 'Total Sales'}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{fmt((salesBreakdown || []).reduce((s, x) => s + (x.amount || 0), 0), currency)}</span>
            </div>
        </div>
        <div>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.muted, marginBottom: 10 }}>
                {t('expenses_from_till') || 'Expenses from This Till'}
            </div>
            {(expensesBreakdown || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13 }}>
                    <span style={{ color: '#475569' }}>{item.label || item.category}</span>
                    <span style={{ fontWeight: 700, color: C.text }}>{fmt(item.amount, currency)}</span>
                </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 13, borderTop: '1px solid #f1f5f9', paddingTop: 6, marginTop: 4 }}>
                <span style={{ color: '#475569', fontWeight: 700 }}>{t('total_expenses') || 'Total Expenses'}</span>
                <span style={{ fontWeight: 700, color: C.text }}>{fmt((expensesBreakdown || []).reduce((s, x) => s + (x.amount || 0), 0), currency)}</span>
            </div>
        </div>
    </div>
);

const DenominationSection = ({ denominations, currency, t }) => {
    const [open, setOpen] = useState(false);
    if (!denominations || denominations.length === 0) return null;
    return (
        <>
            <div
                onClick={() => setOpen(!open)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 14,
                    fontSize: 12, fontWeight: 600, color: C.green, cursor: 'pointer', padding: '8px 0',
                    fontFamily: FONT, userSelect: 'none',
                }}
            >
                <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', display: 'inline-block' }}>&#9654;</span>
                {t('denomination_count') || 'Denomination Count'}
            </div>
            {open && (
                <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: 8, marginTop: 8,
                }}>
                    {denominations.map((d, i) => (
                        <div key={i} style={{
                            background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
                            padding: '8px 12px', textAlign: 'center', fontSize: 12,
                        }}>
                            <div style={{ fontWeight: 800, color: C.text }}>{d.qty} &times;</div>
                            <div style={{ color: C.muted, fontSize: 11 }}>{d.value} {currency} {d.type || 'note'}</div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

const ClosedSessionCard = ({ session, currency, t }) => {
    const isMobile = useIsMobile();
    const isFlagged = session.is_flagged || session.flagged;
    const salesBreakdown = session.sales_breakdown || [
        { label: t('cash_sales') || 'Cash Sales', amount: session.cash_sales ?? 0 },
        { label: t('card_sales') || 'Card Sales', amount: session.card_sales ?? 0 },
    ];
    const expensesBreakdown = session.expenses_breakdown || session.expenses_list || [];

    if (isMobile) {
        const diff = session.cash_difference ?? 0;
        const status = isFlagged
            ? { plain: true, tone: 'red', icon: '▾ ', label: `${fmt(Math.abs(diff), currency)} ${diff < 0 ? (t('short') || 'short') : (t('over') || 'over')}` }
            : { plain: true, tone: 'green', icon: '● ', label: t('balanced') || 'Balanced' };
        return (
            <MobileCard
                accent={isFlagged ? 'red' : undefined}
                avatar={{ initials: getInitials(session.employee_name), tone: 'gray', ping: isFlagged ? 'red' : 'green' }}
                title={session.employee_name}
                amount={fmt(session.total_sales ?? 0, currency)}
                status={status}
                subtitle={`${session.session_id} · ${formatDuration(session.start_time, session.end_time)}`}
            >
                <CashReconBar session={session} currency={currency} t={t} />
                <BreakdownRow salesBreakdown={salesBreakdown} expensesBreakdown={expensesBreakdown} currency={currency} t={t} />
                <DenominationSection denominations={session.denomination_count} currency={currency} t={t} />
            </MobileCard>
        );
    }

    return (
        <div style={{
            background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            borderLeft: isFlagged ? `4px solid ${C.red}` : undefined,
            padding: 24, marginBottom: 16, transition: 'box-shadow 0.15s', fontFamily: FONT,
        }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
        >
            {/* Top row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: C.mutedLight, flexShrink: 0 }} />
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: C.text }}>
                            {session.employee_name}
                            {isFlagged && (
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 4,
                                    fontSize: 11, fontWeight: 700, color: C.red, background: C.redLight,
                                    padding: '3px 8px', borderRadius: 4, marginLeft: 8,
                                }}>&#9888; {t('flagged') || 'Flagged'}</span>
                            )}
                        </div>
                        <div style={{ fontSize: 12, color: C.mutedLight, fontWeight: 500 }}>{session.session_id} &middot; {session.sales_date || ''}</div>
                    </div>
                </div>
                <div style={{ fontSize: 12, color: C.muted, display: 'flex', alignItems: 'center', gap: 12 }}>
                    {formatTime(session.start_time)} &rarr; {formatTime(session.end_time)}
                    <span style={{
                        background: C.bg, padding: '4px 10px', borderRadius: 6,
                        fontWeight: 600, fontSize: 11,
                    }}>{formatDuration(session.start_time, session.end_time)}</span>
                </div>
            </div>

            {/* Cash reconciliation bar */}
            <CashReconBar session={session} currency={currency} t={t} />

            {/* Breakdown */}
            <BreakdownRow salesBreakdown={salesBreakdown} expensesBreakdown={expensesBreakdown} currency={currency} t={t} />

            {/* Denominations */}
            <DenominationSection denominations={session.denomination_count} currency={currency} t={t} />
        </div>
    );
};

const StoreSummaryCard = ({ summary, date, currency, t }) => {
    const isMobile = useIsMobile();
    if (!summary) return null;
    return (
        <div style={{
            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenDark} 100%)`,
            borderRadius: 14, padding: isMobile ? 18 : 28, color: C.white, marginBottom: isMobile ? 16 : 28, fontFamily: FONT,
        }}>
            <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>{t('daily_store_summary') || 'Daily Store Summary'}</div>
            <div style={{ fontSize: 12, opacity: 0.7, marginBottom: isMobile ? 16 : 20 }}>
                {formatDisplayDate(date)} &middot; {summary.total_sessions ?? 0} {t('sessions') || 'sessions'} ({summary.closed_sessions ?? 0} {t('closed') || 'closed'}, {summary.active_sessions ?? 0} {t('active') || 'active'})
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)', gap: isMobile ? 14 : 20, marginBottom: isMobile ? 16 : 20 }}>
                {[
                    { label: t('total_sales') || 'Total Sales', value: fmt(summary.total_sales, currency) },
                    { label: t('total_cash') || 'Total Cash', value: fmt(summary.total_cash, currency) },
                    { label: t('total_expenses') || 'Total Expenses', value: fmt(summary.total_expenses, currency) },
                ].map((s, i) => (
                    <div key={i} style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, opacity: 0.6, marginBottom: 6 }}>{s.label}</div>
                        <div style={{ fontSize: isMobile ? 19 : 22, fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.value}</div>
                    </div>
                ))}
            </div>
            <div className="portal-grid-4" style={{
                borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 16,
                display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16,
            }}>
                {[
                    { label: t('expected_cash') || 'Expected Cash', value: fmt(summary.expected_cash, currency) },
                    { label: t('counted_cash') || 'Counted Cash', value: fmt(summary.counted_cash ?? summary.end_cash, currency) },
                    { label: t('difference') || 'Difference', value: fmt(summary.cash_difference, currency) },
                    { label: t('flagged') || 'Flagged', value: `${summary.flagged_count ?? 0} ${(summary.flagged_count ?? 0) !== 1 ? (t('sessions') || 'sessions') : (t('session') || 'session')}` },
                ].map((s, i) => (
                    <div key={i}>
                        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6, opacity: 0.5, marginBottom: 4 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>{s.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ t, title, message }) => (
    <div style={{
        textAlign: 'center', padding: '60px 20px', fontFamily: FONT,
    }}>
        <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }}>&#128203;</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 8 }}>
            {title || t('no_sessions_for_date') || 'No sessions for this date'}
        </div>
        <div style={{ fontSize: 13, color: C.muted }}>
            {message || t('no_sessions_description') || 'There are no cashier sessions recorded for the selected date.'}
        </div>
    </div>
);

export {
    FONT,
    fmt,
    getInitials,
    formatTime,
    formatDuration,
    formatDisplayDate,
    Modal,
    KpiCard,
    SectionHeader,
    ActiveSessionCard,
    CashReconBar,
    BreakdownRow,
    DenominationSection,
    ClosedSessionCard,
    StoreSummaryCard,
    EmptyState,
};
